/**
 * Push broadcast — compose one message and fan it out to the customer base.
 *
 * Superadmin only (the route guard and the API both enforce). A broadcast picks one
 * notification category; the delivery service skips anyone who has muted that category, so a
 * `marketing` blast respects the marketing switch while `security` reaches everyone. The
 * optional deep link opens a screen in the app when a customer taps the notification, and is a
 * picker over the app's real routes (`contracts/app-links.ts`) rather than a text field: the
 * contract accepts only an in-app path or an `https://` URL, and the free-text field this
 * replaced advertised a `genexpay://` format that both the contract and the app reject. The page
 * surfaces the four categories and a live phone-style preview, and reports how many devices the
 * send will reach. POSTs to `/admin/push/broadcast`.
 *
 * ## The picture, and where it comes from
 *
 * `imageUrl` is optional and its absence is the ordinary case — a text notification is not a
 * degraded one. What it is *not* is a free-text field: both platforms fetch the image from the
 * device after the notification arrives, with no session and no idea which host served this
 * console, so a relative path is a fetch of nothing and a data URI blows past the 4 KB payload
 * ceiling both push services enforce — which does not produce an imageless notification, it
 * produces a **rejected** one, and the words are lost with it.
 *
 * So the source is the platform's own image library: the same store the email campaigns use,
 * already public, already cached, already where an operator uploads artwork. The picker offers
 * the images the server has given an absolute URL for and says plainly why any others cannot
 * be used. A CDN-hosted image is still allowed by pasting the link, because a deployment is
 * entitled to keep its art elsewhere and nothing here should stop it.
 *
 * ## What the operator is told about platforms, and what they are not
 *
 * Android draws the picture with no client code at all — FCM's `notification.image` renders as
 * a `BigPictureStyle` and `expo-notifications` already ships the receiver. iOS cannot: an
 * image there is an *attachment*, and only a Notification Service Extension — a second native
 * target — may fetch one. That extension is added by a config plugin in the app repo and takes
 * effect on the next **native build**, not on the next JavaScript update.
 *
 * The copy on this page says exactly that and does not promise a picture on both platforms.
 * An imageless iOS notification still delivers its title and body correctly, which is the
 * graceful half of the degradation and the reason the two halves could ship apart — but an
 * operator who was told the image would appear and finds it did not is an operator who stops
 * trusting the console's own descriptions.
 */
import { useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { EmailImage, PushCategory } from '../contracts/types.js';
import { APP_LINK_OPTIONS, CUSTOM_APP_LINK } from '../contracts/app-links.js';
import { PUSH_CATEGORIES } from '../contracts/vocab.js';
import { Badge, Button, Card, Field, Input, Select, Textarea } from '../components/ui.js';
import { IconBell, IconSend } from '../components/icons.js';
import { useToast } from '../components/Toast.js';

const CATEGORY_INFO: Readonly<Record<PushCategory, { label: string; hint: string; tone: 'green' | 'violet' | 'red' | 'amber' }>> = {
  money: { label: 'Money', hint: 'Payments, deposits and settlements', tone: 'green' },
  support: { label: 'Support', hint: 'Replies and conversation updates', tone: 'violet' },
  security: { label: 'Security', hint: 'Sign-ins and account safety, always delivered', tone: 'red' },
  marketing: { label: 'Marketing', hint: 'Promotions and product news, respects opt-out', tone: 'amber' },
};

const MAX_TITLE = 120;
const MAX_BODY = 500;

/**
 * The picker value that means "not one of ours".
 *
 * Deliberately not a valid image id or a URL, so it can never collide with a real choice —
 * the same trick `CUSTOM_APP_LINK` uses one field below, for the same reason.
 */
const CUSTOM_IMAGE = '__custom_image__';

export function PushBroadcastPage(): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<PushCategory>('marketing');
  // The picked destination: '' for no link, an in-app path, or the custom sentinel.
  const [destination, setDestination] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [lastReach, setLastReach] = useState<number | null>(null);

  /**
   * The shared image library, loaded once.
   *
   * Not `useAsync`, because a failure here must not take the page down: a broadcast with no
   * picture is the ordinary case, and an operator whose image list would not load should still
   * be able to send the words. So the list simply stays empty and the picker says so.
   */
  const [images, setImages] = useState<readonly EmailImage[]>([]);
  const [imagesFailed, setImagesFailed] = useState(false);
  /** The chosen image's id, `CUSTOM_IMAGE` for a pasted link, or '' for none. */
  const [picture, setPicture] = useState('');
  const [customImage, setCustomImage] = useState('');

  useEffect(() => {
    let live = true;
    api
      .listEmailImages()
      .then((result) => {
        if (live) setImages(result.images);
      })
      .catch(() => {
        if (live) setImagesFailed(true);
      });
    return () => {
      live = false;
    };
  }, [api]);

  const customPicture = picture === CUSTOM_IMAGE;
  const chosen = images.find((image) => image.id === picture) ?? null;
  /*
   * What actually goes on the wire.
   *
   * The server's own absolute URL where it gave one — that is exactly the string the device
   * will fetch, so the preview here and the notification on the phone cannot disagree. The
   * bare id otherwise, which the API resolves against `PUBLIC_API_URL`: an image uploaded
   * before that setting existed has a null `url` on this list and a perfectly resolvable id.
   */
  const imageUrl = customPicture
    ? customImage.trim()
    : chosen === null
      ? ''
      : (chosen.url ?? chosen.id);
  const imageValid =
    imageUrl === '' || !customPicture || customImage.trim().startsWith('https://');
  /** The thumbnail for the preview, which is only ever a URL the browser can load. */
  const previewImage = customPicture ? customImage.trim() : (chosen?.url ?? null);

  const custom = destination === CUSTOM_APP_LINK;
  const deepLink = custom ? customUrl.trim() : destination;
  // The contract accepts an in-app path or an `https://` URL and nothing else. Checking
  // it here rather than letting the API refuse means the operator finds out while they
  // are still looking at the field, not after composing the whole broadcast.
  const linkValid = deepLink === '' || deepLink.startsWith('/') || deepLink.startsWith('https://');
  const canSend = title.trim() !== '' && body.trim() !== '' && linkValid && imageValid;

  async function send() {
    if (!canSend) {
      toast.error('Add a title and body', 'A broadcast needs both a title and a message.');
      return;
    }
    setSending(true);
    try {
      const result = await api.broadcastPush({
        title: title.trim(),
        body: body.trim(),
        category,
        ...(deepLink !== '' ? { url: deepLink } : {}),
        ...(imageUrl !== '' ? { imageUrl } : {}),
      });
      setLastReach(result.recipients);
      toast.success('Broadcast sent', `Reaching ${result.recipients.toLocaleString()} device(s).`);
      setTitle('');
      setBody('');
      setDestination('');
      setCustomUrl('');
      setPicture('');
      setCustomImage('');
    } catch (err) {
      toast.error('Could not broadcast', messageOf(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Push broadcast</h1>
        <p className="page-head__sub">
          Send a notification to every customer. Pick a category. Anyone who muted that category will not receive it.
        </p>
      </div>

      <div className="grid grid--2">
        <Card title="Compose">
          <Field label="Title" htmlFor="push-title">
            <Input
              id="push-title"
              value={title}
              maxLength={MAX_TITLE}
              placeholder="A quick update from us"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field label="Message" htmlFor="push-body">
            <Textarea
              id="push-body"
              value={body}
              rows={4}
              maxLength={MAX_BODY}
              placeholder="Write the notification your customers will see…"
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          <div className="faint" style={{ fontSize: 12, marginTop: -6, textAlign: 'right' }}>
            {body.length}/{MAX_BODY}
          </div>
          <Field label="Picture (optional)" htmlFor="push-image">
            <Select
              id="push-image"
              value={picture}
              onChange={(e) => setPicture(e.target.value)}
            >
              <option value="">No picture</option>
              {/*
                Only the images the server has given an absolute URL. One with a null `url` is
                stored but not addressable from outside — this deployment has no public address
                configured — and offering it would send a notification pointing at nothing.
              */}
              {images
                .filter((image) => image.url !== null)
                .map((image) => (
                  <option key={image.id} value={image.id}>
                    {image.label === '' ? image.altText : image.label}
                  </option>
                ))}
              <option value={CUSTOM_IMAGE}>Paste an https:// image link</option>
            </Select>
          </Field>

          {customPicture && (
            <Field label="Image link" htmlFor="push-image-custom">
              <Input
                id="push-image-custom"
                value={customImage}
                placeholder="https://…"
                onChange={(e) => setCustomImage(e.target.value)}
              />
              {!imageValid && (
                <div className="form-error">
                  An image link must start with <code className="mono">https://</code>. Plain{' '}
                  <code className="mono">http://</code> is dropped by iOS outright, so the
                  picture would appear on one platform and not the other.
                </div>
              )}
            </Field>
          )}

          {/*
            Said before the send, not discovered after it. Android draws the picture with no
            client code; iOS needs a notification-service extension, which is a native build
            rather than a JavaScript update. A notification with an image iOS cannot attach
            still delivers its words, which is why the two can ship apart — but an operator who
            was promised a picture and did not get one stops believing the next description too.
          */}
          {imageUrl !== '' && (
            <p className="muted" style={{ marginTop: -4, fontSize: 12, lineHeight: 1.6 }}>
              Android devices show this picture straight away. iPhones show it from the next
              native app release onwards; until then an iPhone receives the same notification
              without the picture, and the title and message are unaffected.
            </p>
          )}

          {imagesFailed && (
            <p className="muted" style={{ marginTop: -4, fontSize: 12 }}>
              The image library could not be loaded, so only a pasted link is available. Nothing
              else about this broadcast is affected.
            </p>
          )}

          {!imagesFailed && images.length > 0 && images.every((image) => image.url === null) && (
            <p className="muted" style={{ marginTop: -4, fontSize: 12, lineHeight: 1.6 }}>
              The stored images have no public address, so a phone could not fetch one. Set{' '}
              <code className="mono">PUBLIC_API_URL</code> on the API, or paste a full{' '}
              <code className="mono">https://</code> image link.
            </p>
          )}

          <Field label="Deep link (optional)" htmlFor="push-url">
            <Select
              id="push-url"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              <option value="">No link</option>
              {APP_LINK_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              <option value={CUSTOM_APP_LINK}>Custom https:// link</option>
            </Select>
          </Field>

          {custom && (
            <Field label="Custom link" htmlFor="push-url-custom">
              <Input
                id="push-url-custom"
                value={customUrl}
                placeholder="https://…"
                onChange={(e) => setCustomUrl(e.target.value)}
              />
              {!linkValid && (
                <div className="form-error">
                  A custom link must start with <code className="mono">https://</code>. Plain{' '}
                  <code className="mono">http://</code> is refused: a broadcast goes to the whole
                  customer base, and one that opens a plaintext page is one a network attacker
                  rewrites for all of them at once.
                </div>
              )}
            </Field>
          )}

          <div className="field">
            <span className="field__label">Category</span>
            <div className="cat-grid">
              {PUSH_CATEGORIES.map((c) => {
                const info = CATEGORY_INFO[c];
                return (
                  <button
                    key={c}
                    type="button"
                    className={`cat-card${category === c ? ' cat-card--on' : ''}`}
                    onClick={() => setCategory(c)}
                  >
                    <span className="cat-card__top">
                      <Badge tone={info.tone} dot>
                        {info.label}
                      </Badge>
                      {category === c && <span className="cat-card__check">Selected</span>}
                    </span>
                    <span className="cat-card__hint">{info.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="settings-actions" style={{ marginTop: 8 }}>
            <Button variant="primary" loading={sending} disabled={!canSend} onClick={() => void send()}>
              <IconSend size={15} /> Send broadcast
            </Button>
          </div>
        </Card>

        <div className="push-side">
          <Card title="Preview">
            <div className="push-preview">
              <div className="push-notif">
                <span className="push-notif__app">
                  <IconBell size={14} /> {'{platform}'} · now
                </span>
                <span className="push-notif__title">{title.trim() === '' ? 'Notification title' : title}</span>
                <span className="push-notif__body">
                  {body.trim() === '' ? 'Your message preview appears here as customers will see it.' : body}
                </span>
                {/*
                  Only ever a URL a browser can load. A bare image id is a perfectly valid thing
                  to *send* — the API resolves it — and nothing this console can render, so the
                  preview omits it rather than drawing a broken frame beside a live broadcast.
                */}
                {previewImage !== null && previewImage !== '' && (
                  <img
                    className="push-notif__image"
                    src={previewImage}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>
          </Card>

          <Card title="Delivery">
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
              A <strong>{CATEGORY_INFO[category].label.toLowerCase()}</strong> broadcast{' '}
              {category === 'security' ? 'reaches every device. Security alerts are always delivered.' : 'skips anyone who has muted this category.'}
            </p>
            {lastReach !== null && (
              <div className="deliver-stat">
                <span className="deliver-stat__num">{lastReach.toLocaleString()}</span>
                <span className="faint">devices reached by your last broadcast</span>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
